<template>
  <div id="myGraphDiv">
    <div style="display: flex; justify-content: space-between">
      <div id="myDiagramDiv" style="flex-grow: 1"></div>
      <div id="contextMenu" v-show="showingContextMenu">
        <ContextMenu
          v-bind:isTester="isTester"
          v-bind:clickedObject="clickedObject"
          v-bind:layoutInline="layoutInline"
          v-bind:bus="bus"
          ref="ContextMenu"
        ></ContextMenu>
      </div>
    </div>
    <div style="display: flex; justify-content: space-between">
      <div id="myOverviewDiv"></div>
    </div>
  </div>
</template>

<script>

import * as go from 'gojs'
import { finishDrop, makeGroupsBackgroundAlternate } from '@/graph_parts/moving'
import { defineCustomShapes } from '@/graph_parts/shapes'
import { createGroupTemplate } from '@/graph_parts/group_template'
import { createNodeTemplate } from '@/graph_parts/node_template'
import { createOperatorTemplate } from '@/graph_parts/operator_template'
import { createDiagram, shouldUpdate, goLicenseKey } from '@/graph_parts/tds_diagram'
import { calculateExpandedDepth, calculateDepth } from '@/graph_parts/depth'
import { getTreeDataFromDiagram, getTreeDataForNode } from '@/graph_parts/tree_data'
import { emitterSingleton } from '@/graph_parts/emitter'
import { getDiagramBottomLeft } from '@/graph_parts/common_template_support'
import uuid from 'uuid'
import { defaultNodeAttributes, defaultGroupAttributes, spacingVerticalForTopLevel,
  defaultGroupName, offsetGroupX, offsetGroupY, objectTypes } from '@/utils/constants'
import ContextMenu from './parts/ContextMenu'
import { removeOperators, insertOperators } from '@/graph_parts/operators'

let myDiagram = null
let myEmitter = emitterSingleton.instance
let $ = go.GraphObject.make
let keepQuietAboutChanges = false

function expandGroups (g, i, level) {
  if (!(g instanceof go.Group)) return
  g.isSubGraphExpanded = i < level
  g.memberParts.each(function (m) {
    expandGroups(m, i + 1, level)
  })
}

function reexpand (level) {
  // myDiagram.startTransaction('reexpand')
  myDiagram.findTopLevelGroups().each(function (g) { expandGroups(g, 0, level) })
  // myDiagram.commitTransaction('reexpand')
}

export default {
  name: 'graph',
  components: {
    ContextMenu
  },
  props: ['queryJson', 'bus', 'isTester', 'snapToGrid', 'layoutInline'],
  data () {
    return {
      suggestTermsTerm: '',
      suggestTermsOtherTerms: [],
      clickedObject: null,
      showingContextMenu: false,
      cxElement: null,
    }
  },
  mounted () {
    this.tdsInit()
  },
  created () {
    this.bus.$on('clear', () => {
      myDiagram.model = go.Model.fromJson({})
      myDiagram.layoutDiagram(true)
    })
    this.bus.$on('reset', () => {
      myDiagram.model = go.Model.fromJson(this.queryJson)
      myDiagram.layoutDiagram(true)
    })
    this.bus.$on('relayout', () => {
      myDiagram.requestUpdate()
    })
    this.bus.$on('increaseSemanticLevel', () => {
      const d = calculateExpandedDepth(myDiagram)
      const m = calculateDepth(myDiagram)
      if (d < m) {
        reexpand(d + 1)
        myDiagram.layoutDiagram(true)
      }
    })
    this.bus.$on('decreaseSemanticLevel', () => {
      const d = calculateExpandedDepth(myDiagram)
      if (d > 0) {
        reexpand(d - 1)
        myDiagram.layoutDiagram(true)
      }
    })
    this.bus.$on('undo', () => {
      myDiagram.commandHandler.undo()
    })
    this.bus.$on('redo', () => {
      myDiagram.commandHandler.redo()
    })
    this.bus.$on('addData', (newData) => {
      // myDiagram.startTransaction('addData')
      myDiagram.model.addNodeDataCollection(this.adjustImportedData(newData))
      this.setOverallLayout(myDiagram.tdsOverallLayout)
      // myDiagram.commitTransaction('addData')
      myDiagram.layoutDiagram(true)
    })
    this.bus.$on('addTerms', (newData, termKey, groupKey) => {
      this.processAddTerms(newData, termKey, groupKey)
    })
    this.bus.$on('setCounts', counts => {
      this.processSetCounts(counts)
    })
    this.bus.$on('clearCounts', () => {
      this.processClearCounts()
    })
    this.bus.$on('setDuplicates', (duplicatesData) => {
      this.processSetDuplicates(duplicatesData)
    })
    this.bus.$on('centreDiagram', () => {
      const topLevelGroups = myDiagram.findTopLevelGroups()
      // myDiagram.alignDocument(go.Spot.TopLeft, go.Spot.TopLeft)
      myDiagram.alignDocument(new go.Spot(0, 0, -40, 0), new go.Spot(0, 0, -40, 0))
      if (topLevelGroups && topLevelGroups.count === 1) {
        const d = calculateExpandedDepth(myDiagram)
        let level = d - 1
        while (level >= 0 && !myDiagram.viewportBounds.containsRect(myDiagram.documentBounds)) {
          topLevelGroups.each(function (g) { expandGroups(g, 0, level) })
          myDiagram.layoutDiagram(true)
          level--
        }
      }
    })
    this.bus.$on('outputData', () => { this.outputData() })
    this.bus.$on('addSimpleTree', (simpleTree) => {
      this.processAddSimpleTree(simpleTree)
    })
    this.bus.$on('setOverallLayout', (overallLayout) => {
      this.setOverallLayout(overallLayout)
    })
    this.bus.$on('updateText', keyText => {
      const n = myDiagram.findNodeForKey(keyText.key)
      if (n) {
        myDiagram.model.setDataProperty(n.data, 'text', keyText.text)
      }
    })
  },
  watch: {
    queryJson: async function (v) {
      if (v) {
        myDiagram.model = go.Model.fromJson(v)
        myDiagram.layoutDiagram(true)
        myEmitter.emitter.bus.$emit('initialDataLoaded')
      }
    },
    layoutInline: function (v) {
      myDiagram.tdsLayoutInline = v || false
    },
    snapToGrid: function (v) {
      this._setSnapCellSize(v)
    }
  },
  methods: {
    tdsInit () {
      myEmitter.emitter = this

      go.licenseKey = goLicenseKey

      myDiagram = createDiagram('myDiagramDiv', 'myOverviewDiv', true, false)
      myDiagram.tdsOverallLayout = 'nested'

      defineCustomShapes()

      const groupTemplate = createGroupTemplate(this, this.isTester)
      groupTemplate.mouseDrop = finishDrop

      myDiagram.groupTemplateMap.add('OfGroups', groupTemplate)

      const operatorTemplate = createOperatorTemplate()

      const nodeTemplate = createNodeTemplate(this.isTester, true, true)
      nodeTemplate.mouseDrop = finishDrop

      const templateMap = new go.Map('string', go.Node)
      templateMap.add('', nodeTemplate)
      templateMap.add('Operator', operatorTemplate)
      myDiagram.nodeTemplateMap = templateMap

      myDiagram.addModelChangedListener(function (e) {
        if (!e.isTransactionFinished || keepQuietAboutChanges) {
          return
        }
        if (shouldUpdate(e)) {
          myEmitter.emitter.bus.$emit('queryChanged')
        }
      })

      this.cxElement = document.getElementById('contextMenu')
      const myContextMenu = $(go.HTMLInfo, {
        show: this.tdsShowContextMenu,
        hide: this.tdsHideContextMenu,
        mainElement: this.cxElement
      })
      nodeTemplate.contextMenu = myContextMenu
      groupTemplate.contextMenu = myContextMenu
      myDiagram.contextMenu = myContextMenu
      this.cxElement.addEventListener('contextmenu', function (e) {
        e.preventDefault()
        return false
      }, false)

      myDiagram.focus()
    },
    getJson () {
      const jsonModel = JSON.parse(myDiagram.model.toJson())
      const r = {}
      r.class = jsonModel.class
      r.linkDataArray = jsonModel.linkDataArray
      r.nodeDataArray = []
      jsonModel.nodeDataArray.forEach(node => {
        if (!(node.hasOwnProperty('isOperator') && node.isOperator)) {
          const newItem = {...node}
          if (node.hasOwnProperty('isGroup') && node.isGroup) {
            newItem.childrenPrefixOperatorVisible = false
            newItem.gridLayout = 'nested'
          }
          r.nodeDataArray.push(newItem)
        }
      })
      return JSON.stringify(r)
    },
    getImage () {
      return myDiagram.makeImageData({ scale: 1.0 })
    },
    outputData (part) {
      let i = null
      if (typeof part === 'undefined') {
        console.log('outputData-----')
        const j = myDiagram.nodes
        while (j.next()) {
          if (j.value.isTopLevel) {
            this.outputData(j.value)
          }
        }
        i = myDiagram.findTopLevelGroups()
      } else if (part instanceof go.Group) {
        console.log('Group', part.data.text, part.placeholder.getDocumentPoint(go.Spot.TopLeft), part.naturalBounds, part.actualBounds)
        i = part.memberParts
      } else if (part instanceof go.Node) {
        console.log('Node', part.data.text, part.naturalBounds, part.actualBounds)
      }
      if (i !== null) {
        while (i.next()) {
          this.outputData(i.value)
        }
      }
    },
    getTreeData (topLevelOperator) {
      const children = getTreeDataFromDiagram(myDiagram)
      const op = (topLevelOperator === null || topLevelOperator === '') ? 'AND' : topLevelOperator
      return {text: 'query', children: children, operator: op}
    },
    getTreeDataForNode (node) {
      return getTreeDataForNode(node)
    },
    getStatistics () {
      const a = {
        terms: 0,
        fieldTags: 0,
        operators: 0,
        distinctTerms: new Set(),
        distinctFieldTags: new Set(),
        distinctOperators: new Set()
      }
      this._accumulateStatistics(a, getTreeDataFromDiagram(myDiagram))
      return {
        terms: a.terms,
        fieldTags: a.fieldTags,
        operators: a.operators,
        distinctTerms: a.distinctTerms.size,
        distinctFieldTags: a.distinctFieldTags.size,
        distinctOperators: a.distinctOperators.size
      }
    },
    _accumulateStatistics (a, data) {
      data.forEach(item => {
        if (item.operator) {
          a.distinctOperators.add(item.operator.toLowerCase())
          a.operators++
        }
        if (item.isNegative) {
          a.distinctOperators.add('negation')
          a.operators++
        }
        if (item.fieldSpecifier) {
          a.distinctFieldTags.add(item.fieldSpecifier.toLowerCase())
          a.fieldTags++
        }
        if (item.isGroup) {
          this._accumulateStatistics(a, item.children)
        } else {
          a.distinctTerms.add(item.text)
          a.terms++
        }
      })
      return a
    },
    adjustImportedData (data) {
      const dbBottomLeft = getDiagramBottomLeft(myDiagram)
      const importedTopLeft = {x: null, y: null}
      for (let i of data) {
        if (importedTopLeft.x === null || i.position.x < importedTopLeft.x) {
          importedTopLeft.x = i.position.x
        }
        if (importedTopLeft.y === null || i.position.y < importedTopLeft.y) {
          importedTopLeft.y = i.position.y
        }
        const oldKey = i.key
        i.key = uuid()
        if (i.isGroup) {
          for (let j of data) {
            if (j.group === oldKey) {
              j.group = i.key
            }
          }
        }
      }
      for (let i of data) {
        i.position = new go.Point(
          i.position.x - importedTopLeft.x + dbBottomLeft.x,
          i.position.y - importedTopLeft.y + dbBottomLeft.y
        )
      }
      return data
    },
    processSetCounts (counts) { // Put the count data in place so that the hit count is shown
      // This code block should not be wrapped with a transaction so that undo/redo works without setting count as a separate undo transaction
      if (counts && Array.isArray(counts)) {
        counts.forEach(x => {
          const n = myDiagram.findNodeForKey(x.key)
          if (typeof n !== 'undefined' && n !== null) {
            myDiagram.model.setDataProperty(n.data, '_count', x.count)
            myDiagram.model.setDataProperty(n.data, '_showingCount', true)
          }
        })
      }
    },
    processClearCounts () {
      // This code block should not be wrapped with a transaction so that undo/redo works without setting count as a separate undo transaction
      const topLevelItemsIterator = myDiagram.findTreeRoots()
      while (topLevelItemsIterator.next()) {
        const x = topLevelItemsIterator.value
        const n = myDiagram.findNodeForKey(x.key)
        if (n) {
          myDiagram.model.setDataProperty(n.data, '_count', null)
          myDiagram.model.setDataProperty(n.data, '_showingCount', null)
        }
      }
    },
    processSetDuplicates (duplicates) {
      if (duplicates) {
        keepQuietAboutChanges = true
        // myDiagram.startTransaction('processSetDuplicates')
        const ni = myDiagram.nodes
        while (ni.next()) {
          const x = ni.value
          myDiagram.model.setDataProperty(x.data, '_duplicated', false)
        }
        duplicates.forEach(d => {
          const n = myDiagram.findNodeForKey(d.key)
          if (n) {
            myDiagram.model.setDataProperty(n.data, '_duplicated', true)
          }
        })
        // myDiagram.commitTransaction('processSetDuplicates')
        keepQuietAboutChanges = false
      }
    },
    processAddTerms (newData, termKey, groupKey) {
      // myDiagram.startTransaction('processAddTerms')
      const g = groupKey ? myDiagram.findNodeForKey(groupKey) : null
      let ordering = 10
      if (g) {
        const t = myDiagram.findNodeForKey(termKey)
        const gi = g.memberParts.iterator
        while (gi.next()) {
          const x = gi.value
          if (x.data.ordering && x.data.ordering > t.data.ordering) {
            const newOrdering = x.data.ordering + newData.length * 10
            myDiagram.model.setDataProperty(x.data, 'ordering', newOrdering)
          }
        }
        ordering = t.data.ordering + 10
      }
      const newNodes = []
      const dbBottomLeft = getDiagramBottomLeft(myDiagram)
      let [x, y] = [dbBottomLeft.x, dbBottomLeft.y]
      newData.forEach(i => {
        const a = {
          ...defaultNodeAttributes
        }
        if (g) {
          a.group = groupKey
        } else {
          a.position = new go.Point(x, y + spacingVerticalForTopLevel)
          x += 10
          y += 10
        }
        const newNodeData = {
          key: uuid(),
          text: i,
          ...a,
          ordering: ordering
        }
        newNodes.push(newNodeData)
        ordering += 10
      })
      myDiagram.model.addNodeDataCollection(newNodes)
      // myDiagram.commitTransaction('processAddTerms')
    },
    _processSimpleTreeToNodes (simpleTree, groupKey, newNodes, ordering, x, y, nestingLevel) {
      if (typeof simpleTree.isGroup !== 'undefined' && simpleTree.isGroup) {
        const a = {
          ...defaultGroupAttributes
        }
        if (groupKey) {
          a.group = groupKey
        }
        const thisGroupKey = uuid()
        const n = {
          ...a,
          key: thisGroupKey,
          text: simpleTree.text || defaultGroupName,
          operator: simpleTree.operator,
          ordering: ordering
        }
        if (simpleTree.isNegative) {
          n.isNegative = true
        }
        if (simpleTree.fieldSpecifier) {
          n.fieldSpecifier = simpleTree.fieldSpecifier
        }
        newNodes.push(n)
        let childOrdering = ordering + 10
        simpleTree.children.forEach(i => {
          this._processSimpleTreeToNodes(i, thisGroupKey, newNodes, childOrdering, x, y, nestingLevel + 1)
          childOrdering += 10
        })
      } else {
        const n = {
          key: uuid(),
          text: simpleTree.text,
          ...defaultNodeAttributes,
          ordering: ordering
        }
        if (groupKey) {
          n.group = groupKey
        }
        if (simpleTree.isNegative) {
          n.isNegative = true
        }
        if (simpleTree.fieldSpecifier) {
          n.fieldSpecifier = simpleTree.fieldSpecifier
        }
        x += offsetGroupX * nestingLevel
        y += offsetGroupY * nestingLevel
        n.position = new go.Point(x, y + spacingVerticalForTopLevel)
        newNodes.push(n)
      }
    },
    processAddSimpleTree (simpleTree) {
      if (typeof simpleTree !== 'undefined') {
        const newNodes = []
        const dbBottomLeft = getDiagramBottomLeft(myDiagram)
        let [x, y] = [dbBottomLeft.x, dbBottomLeft.y]
        this._processSimpleTreeToNodes(simpleTree, null, newNodes, 10, x, y, 0)
        myDiagram.model.addNodeDataCollection(newNodes)
        makeGroupsBackgroundAlternate(myDiagram)
      }
    },
    setOverallLayout (overallLayout) {
      // If you change this function you may need to change getJson() too.
      const ni = myDiagram.nodes
      const nodes = []
      while (ni.next()) {
        const x = ni.value
        if (x.data.isGroup) {
          nodes.push(x)
        }
      }
      const topLevelItemsIterator = myDiagram.findTreeRoots()
      while (topLevelItemsIterator.next()) {
        const x = topLevelItemsIterator.value
        if (!x.data.isGroup && !x.data.isOperator) {
          nodes.push(x)
        }
      }
      const setInline = overallLayout === 'inline'
      nodes.forEach(x => {
        myDiagram.model.setDataProperty(x.data, 'childrenPrefixOperatorVisible', setInline)
        removeOperators(x, myDiagram)
        if (setInline) {
          insertOperators(x, myDiagram, x.data.operator, x.data.key)
          myDiagram.model.setDataProperty(x.data, 'gridLayout', 'inline')
        } else {
          myDiagram.model.setDataProperty(x.data, 'gridLayout', 'nested')
        }
      })
      myDiagram.tdsOverallLayout = overallLayout
      myDiagram.layout.invalidateLayout()
    },
    tdsShowContextMenu (obj, diagram, tool) {
      this.clickedObject = {diagram: diagram}
      if (obj === null) {
        this.clickedObject.type = objectTypes.canvas
      } else {
        if (obj.part.data.isGroup) {
          this.clickedObject.type = objectTypes.group
        } else {
          this.clickedObject.type = objectTypes.node
        }
        this.clickedObject.data = obj.part.data
      }
      const mousePt = diagram.lastInput.viewPoint
      this.cxElement.style.left = mousePt.x + 'px'
      this.cxElement.style.top = mousePt.y + 'px'
      this.showingContextMenu = true
      this.$nextTick(() => {
        const fudgeHeight = 55
        const y = mousePt.y + this.$refs.ContextMenu.$el.offsetHeight > window.innerHeight - fudgeHeight
          ? mousePt.y - this.$refs.ContextMenu.$el.offsetHeight
          : mousePt.y
        this.cxElement.style.top = y + 'px'
      })
    },
    tdsHideContextMenu () {
      this.clickedObject = null
      this.showingContextMenu = false
    },
    _setSnapCellSize (v) {
      const enabled = v !== null && typeof v.size !== 'undefined' && v.size > 0
      myDiagram.toolManager.draggingTool.isGridSnapEnabled = enabled
      myDiagram.toolManager.resizingTool.isGridSnapEnabled = enabled
      if (enabled) {
        myDiagram.toolManager.draggingTool.gridSnapCellSize = new go.Size(v.size, v.size)
        myDiagram.toolManager.resizingTool.gridSnapCellSize = new go.Size(v.size, v.size)
      }
    }
  }
}
</script>

<!-- Setting scoped here seems to stop #myDiagramDiv > textarea being picked up on display i.e. when editing name -->
<style lang="scss">
#myGraphDiv {
  position: relative;
}
#myDiagramDiv > textarea {
  background-color: $grey-200;
  min-width: 100px;
}
#myDiagramDiv canvas {
  outline: none;
}
#myOverviewDiv {
  position: absolute;
  width:200px;
  height:100px;
  top: 10px;
  right: 10px;
  z-index: 85;
  border: solid 1px white;
  display: none;
}
#myToolbarDiv {
  text-align: left;
}
#contextMenu {
  z-index: 10001;
  position: absolute;
  left: 5px;
  border: 2px solid $grey-200;
  background-color: #F5F5F5;
  display: block;
  box-shadow: 8px 8px 8px 0 rgba( 0, 0, 0, .5 );
  font-size: 12px;
  font-family: sans-serif;
  font-weight: bold;
}
#contextMenu .list {
  color: $grey-800;
  padding: 0;
}
#contextMenu .list__tile {
  height: 40px;
}
#contextMenu .list__tile:hover {
  background-color: $grey-200;
}
</style>



// WEBPACK FOOTER //
// src/components/Graph.vue